/**
 * @file Frame of the AI Mapping Review panel: a fixed `FlexCard` mirroring
 * the `Agent` chat chrome (translateX slide, token z-indexes), anchored left of
 * the chat (`right: 20 + agentWidth + 15`) with an expand-to-full-width toggle.
 * `uiState.mappingReviewPanel` owns the width — persisted, pointer- and
 * keyboard-driven, and re-clamped against a cap that reserves the live
 * `uiState.agentSidebar.width`, so widening the chat narrows this panel instead of
 * overlapping it. Both widths are read straight off the store, which is why this
 * panel no longer needs a common ancestor with the chat. Auto-opens via `mappingReviewState.panel.open`
 * (set by `openMappingReview`), capturing the chat opener and moving focus to
 * the panel heading on open. A dismissal (Esc / close — the approval stays
 * pending and the opener's chat card is still mounted) restores focus to that
 * opener; a resolution (approve / cancel — the chat card unmounts, so the
 * opener is gone) instead lands focus on the chat composer. While closed the
 * off-screen panel is `inert` so focus can't strand on it. Esc or
 * the close button dismiss WITHOUT resolving — the approval stays pending and
 * the chat card reopens the same session; a dirty dismissal shows a
 * non-destructive "edits are kept" toast. Esc presses originating inside an
 * AntD overlay (Drawer/Modal/Popover/dropdown) are left to that overlay so
 * layered dismissal works. A polite live region announces section switches and
 * moves focus into the revealed surface. Hosts the section nav and the loaded
 * surface: a single table slice for an entity-records import, or the Form
 * values / Tables surfaces for a whole document. Mounted in `ProtectedRoute`
 * beside `Agent`. The card and resize-handle heights follow the dynamic viewport
 * (`dvh`), so a phone's browser bars never clip the panel.
 */
import {
  CSSProperties,
  FC,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Alert, Button, Collapse, Grid, Spin, Typography, theme } from 'antd';
import {
  CloseOutlined,
  CompressOutlined,
  ExpandOutlined,
} from '@ant-design/icons';
import { useSnapshot } from 'valtio';
import type { RowClassificationTag } from '#types';
import FlexBox from '../../atoms/FlexBox';
import FlexCard from '../../atoms/FlexCard';
import FlexCol from '../../atoms/FlexCol';
import { clampPanelWidth, panelViewportClamp, uiState } from '@/store/uiState';
import PanelResizeHandle from '../../atoms/PanelResizeHandle';
import { MessageContext } from '@/providers/MessageContext';
import { EntitiesContext } from '@/providers/artifacts/EntitiesContext';
import { mappingReviewState } from './mappingReviewState';
import MappingReviewSections from './MappingReviewSections';
import type { ReviewSection } from './MappingReviewSections';
import MappingReviewColumns from './MappingReviewColumns';
import type { ReferenceValuesTarget } from './MappingReviewColumns';
import MappingReviewBulkBar from './MappingReviewBulkBar';
import MappingReviewRows from './MappingReviewRows';
import MappingReviewFields from './MappingReviewFields';
import MappingReviewTables from './MappingReviewTables';
import MappingReviewApprove from './MappingReviewApprove';
import { useTargetFields } from './useTargetFields';
import type { MappingReviewTableSlice } from './mappingReviewState';
import type { MappingTarget } from '#types';

const { Title, Paragraph } = Typography;

const HEADING_ID = 'mappingReviewHeading';
const CHAT_COMPOSER_SELECTOR = '[aria-label="model-prompt-input"]';

const srOnly: CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  margin: -1,
  padding: 0,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

/** The single editable slice of an entity-records import: columns + bulk + rows over the entity's record fields. */
const ImportSurface: FC<{
  slice: MappingReviewTableSlice;
  target: Extract<MappingTarget, { kind: 'entityRecords' }>;
  tagFilter: RowClassificationTag | null;
  referenceValues: ReferenceValuesTarget | null;
  onOpenReferenceValues: (target: ReferenceValuesTarget) => void;
  onCloseReferenceValues: () => void;
}> = ({
  slice,
  target,
  tagFilter,
  referenceValues,
  onOpenReferenceValues,
  onCloseReferenceValues,
}) => {
  const { loading, fields } = useTargetFields(target);
  return (
    <>
      <MappingReviewColumns
        slice={slice}
        targetFields={fields}
        fieldsLoading={loading}
        referenceValues={
          referenceValues?.groupId === slice.groupId ? referenceValues : null
        }
        onOpenReferenceValues={onOpenReferenceValues}
        onCloseReferenceValues={onCloseReferenceValues}
      />
      <FlexBox noGrow justifyStart>
        <MappingReviewBulkBar slice={slice} />
      </FlexBox>
      <MappingReviewRows
        slice={slice}
        targetFields={fields}
        tagFilter={tagFilter}
        onOpenReferenceValues={onOpenReferenceValues}
      />
    </>
  );
};

const MappingReviewPanel: FC = () => {
  const { token } = theme.useToken();
  const screens = Grid.useBreakpoint();
  const { messageApi } = useContext(MessageContext);
  const { entities } = useContext(EntitiesContext);
  const snap = useSnapshot(mappingReviewState);
  const { width: storedWidth } = useSnapshot(uiState.mappingReviewPanel, {
    sync: true,
  });
  const { width: storedAgentWidth } = useSnapshot(uiState.agentSidebar, {
    sync: true,
  });
  const agentWidth = clampPanelWidth('agentSidebar', storedAgentWidth);
  const width = clampPanelWidth('mappingReviewPanel', storedWidth);
  const [referenceValues, setReferenceValues] =
    useState<ReferenceValuesTarget | null>(null);
  const [tagFilter, setTagFilter] = useState<RowClassificationTag | null>(null);
  const [section, setSection] = useState<ReviewSection>('fields');
  const [announcement, setAnnouncement] = useState('');
  const session = mappingReviewState.session;
  const open = snap.panel.open && snap.session !== null;
  const headingRef = useRef<HTMLElement>(null);
  const openerRef = useRef<HTMLElement | null>(null);
  const wasOpenRef = useRef(false);
  const regionRef = useRef<HTMLDivElement>(null);
  const prevSectionRef = useRef<ReviewSection>(section);

  const requestClose = useCallback(() => {
    if (mappingReviewState.session?.dirty) {
      messageApi?.info?.('Review paused — your edits are kept');
    }
    mappingReviewState.panel.open = false;
  }, [messageApi]);

  useEffect(() => {
    if (open) {
      wasOpenRef.current = true;
      openerRef.current =
        document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null;
      headingRef.current?.focus();
      return;
    }
    if (!wasOpenRef.current) return;
    wasOpenRef.current = false;
    if (mappingReviewState.session === null) {
      const composer = document.querySelector(CHAT_COMPOSER_SELECTOR);
      if (composer instanceof HTMLElement) composer.focus();
    } else {
      openerRef.current?.focus();
    }
    openerRef.current = null;
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      const target = event.target instanceof HTMLElement ? event.target : null;
      if (
        target?.closest(
          '.ant-drawer, .ant-modal, .ant-popover, .ant-select-dropdown, .ant-dropdown',
        )
      ) {
        return;
      }
      requestClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, requestClose]);

  useEffect(() => {
    if (prevSectionRef.current === section) return;
    prevSectionRef.current = section;
    setAnnouncement(
      section === 'fields' ? 'Showing Form values' : 'Showing Tables',
    );
    const active = document.activeElement;
    const fromNav =
      active instanceof HTMLElement &&
      Boolean(active.closest('[aria-label="Select review section"]'));
    if (!fromNav) regionRef.current?.focus({ preventScroll: true });
  }, [section]);

  if (!session || !snap.session) return null;

  const expanded = snap.panel.expanded;
  const anchoredRight = 20 + agentWidth + 15;

  const sessionSnap = snap.session;
  const target = sessionSnap.target;
  const title =
    sessionSnap.loading && !target
      ? 'Loading review…'
      : !target
        ? 'Import review'
        : target.kind === 'document'
          ? target.binding.kind === 'formVersion'
            ? 'Apply to form'
            : 'Apply to record'
          : `Import into "${entities[target.entityId]?.description?.shortLabel ?? target.entityId}"`;

  const fullWidth = expanded || !screens.md;
  const transition = 'transform 0.3s ease';
  const positionStyle = fullWidth
    ? { left: 20, right: 20, width: 'auto' as const }
    : {
        right: anchoredRight,
        width,
        maxWidth: panelViewportClamp('mappingReviewPanel'),
      };

  const ready =
    !sessionSnap.loading &&
    !sessionSnap.loadError &&
    Boolean(sessionSnap.target);
  const isDocument = sessionSnap.target?.kind === 'document';
  const importSlice = session.import;

  return (
    <>
      <FlexCard
        variant="outlined"
        role="region"
        aria-labelledby={HEADING_ID}
        inert={!open}
        style={{
          position: 'fixed',
          top: 20,
          height: 'calc(100dvh - 150px)',
          transition,
          transform: open ? 'translateX(0)' : 'translateX(-120vw)',
          zIndex: fullWidth
            ? token.zIndexPopupBase + 2
            : token.zIndexPopupBase + 1,
          boxShadow: token.boxShadow,
          paddingTop: 0,
          ...positionStyle,
        }}
        styles={{
          body: {
            overflowY: 'auto',
            height: '100%',
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            paddingTop: 0,
          },
        }}
      >
        <FlexBox
          noGrow
          stretch
          style={{
            position: 'sticky',
            top: 0,
            background: token.colorBgContainer,
            paddingTop: 10,
            paddingBottom: 10,
            borderBottom: `1px solid ${token.colorBorderSecondary}`,
            zIndex: 25,
          }}
        >
          <Title
            level={2}
            id={HEADING_ID}
            ref={headingRef}
            tabIndex={-1}
            style={{ margin: 0 }}
          >
            {title}
          </Title>
          <FlexBox noGrow gap={5}>
            <Button
              type="text"
              size="small"
              icon={expanded ? <CompressOutlined /> : <ExpandOutlined />}
              onClick={() => {
                mappingReviewState.panel.expanded = !expanded;
              }}
              aria-label={
                expanded
                  ? 'Restore review panel width'
                  : 'Expand review panel to full width'
              }
            />
            <Button
              type="text"
              size="small"
              icon={<CloseOutlined />}
              onClick={requestClose}
              aria-label="Close mapping review panel"
            />
          </FlexBox>
        </FlexBox>
        <div aria-live="polite" style={srOnly}>
          {announcement}
        </div>
        <FlexCol stretch gap={10} style={{ width: '100%' }}>
          <MappingReviewSections
            session={session}
            section={section}
            onSectionChange={setSection}
            tagFilter={tagFilter}
            onTagFilterChange={setTagFilter}
          />
          <FlexCol ref={regionRef} tabIndex={-1} gap={10}>
            {sessionSnap.loading && (
              <FlexBox justifyCenter style={{ padding: 20 }}>
                <Spin aria-label="Loading mapping review source" />
              </FlexBox>
            )}
            {sessionSnap.loadError && (
              <Alert
                type="error"
                showIcon
                title="We couldn't load this review"
                description={
                  <Collapse
                    ghost
                    size="small"
                    items={[
                      {
                        key: 'details',
                        label: 'Technical details',
                        children: (
                          <Paragraph copyable style={{ marginBottom: 0 }}>
                            {sessionSnap.loadError}
                          </Paragraph>
                        ),
                      },
                    ]}
                  />
                }
              />
            )}
            {ready &&
              isDocument &&
              session.target?.kind === 'document' &&
              section === 'fields' && (
                <MappingReviewFields
                  session={session}
                  target={session.target}
                />
              )}
            {ready &&
              isDocument &&
              session.target?.kind === 'document' &&
              section === 'tables' && (
                <MappingReviewTables
                  session={session}
                  target={session.target}
                  tagFilter={tagFilter}
                  referenceValues={referenceValues}
                  onOpenReferenceValues={setReferenceValues}
                  onCloseReferenceValues={() => setReferenceValues(null)}
                />
              )}
            {ready &&
              !isDocument &&
              importSlice &&
              session.target?.kind === 'entityRecords' && (
                <ImportSurface
                  slice={importSlice}
                  target={session.target}
                  tagFilter={tagFilter}
                  referenceValues={referenceValues}
                  onOpenReferenceValues={setReferenceValues}
                  onCloseReferenceValues={() => setReferenceValues(null)}
                />
              )}
          </FlexCol>
        </FlexCol>
        <MappingReviewApprove
          session={session}
          section={section}
          onSectionChange={setSection}
          onOpenReferenceValues={setReferenceValues}
        />
      </FlexCard>
      {open && !fullWidth && (
        <PanelResizeHandle
          panel="mappingReviewPanel"
          anchor="right"
          label="Resize mapping review panel"
          style={{
            position: 'fixed',
            top: 20,
            height: 'calc(100dvh - 120px)',
            right: anchoredRight + width - 3,
            width: 6,
            zIndex: token.zIndexPopupBase + 2,
            '--panel-grip-color': token.colorBorder,
            '--panel-grip-color-active': token.colorPrimary,
            '--panel-grip-radius': `${token.borderRadiusSM}px`,
          }}
        />
      )}
    </>
  );
};

export default MappingReviewPanel;
